import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  TextField,
  IconButton,
  Divider,
  Badge,
  CircularProgress,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import api from '../utils/authUtils';

function Messages() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newChatDialog, setNewChatDialog] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  const pollingInterval = useRef(null);

  useEffect(() => {
    fetchConversations();
    fetchUsers();

    // Auto-refresh every 5 seconds
    pollingInterval.current = setInterval(() => {
      if (selectedConversation) {
        fetchMessages(selectedConversation.id, true);
      }
      fetchConversations(true);
    }, 5000);

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await api.get('/conversations');
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Fetch users for new conversation (you might need a specific endpoint)
      const response = await api.get('/users?limit=100');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMessages = async (conversationId, silent = false) => {
    try {
      const response = await api.get(`/conversations/${conversationId}/messages`);
      setMessages(response.data.messages || []);

      // Mark as read
      await api.put(`/conversations/${conversationId}/read`);

      // Update conversation unread count
      if (!silent) {
        fetchConversations(true);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    setMessages([]);
    fetchMessages(conversation.id);
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation || sending) return;

    try {
      setSending(true);
      const response = await api.post(`/conversations/${selectedConversation.id}/messages`, {
        content: messageInput.trim()
      });

      setMessages([...messages, response.data.data]);
      setMessageInput('');
      fetchConversations(true); // Update last message in list
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewConversation = async () => {
    if (!selectedUser) return;

    try {
      const response = await api.post('/conversations', {
        participant_id: selectedUser.id
      });

      setConversations([response.data.conversation, ...conversations]);
      setSelectedConversation(response.data.conversation);
      setNewChatDialog(false);
      setSelectedUser(null);
      fetchMessages(response.data.conversation.id);
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString();
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const participant = conv.participants[0];
    return participant?.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           participant?.full_name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3, height: 'calc(100vh - 150px)' }}>
      <Paper elevation={3} sx={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
        {/* Conversations List */}
        <Box
          sx={{
            width: { xs: '100%', md: '350px' },
            borderRight: { md: '1px solid #e0e0e0' },
            display: { xs: selectedConversation ? 'none' : 'flex', md: 'flex' },
            flexDirection: 'column',
            height: '100%'
          }}
        >
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Messages
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Conversations List */}
          <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
            {filteredConversations.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <MessageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  No conversations yet
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setNewChatDialog(true)}
                  sx={{ mt: 2 }}
                >
                  Start Chat
                </Button>
              </Box>
            ) : (
              filteredConversations.map((conv) => {
                const participant = conv.participants[0];
                return (
                  <ListItem
                    key={conv.id}
                    button
                    selected={selectedConversation?.id === conv.id}
                    onClick={() => handleConversationSelect(conv)}
                    sx={{
                      borderBottom: '1px solid #f0f0f0',
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Badge badgeContent={conv.unread_count} color="error">
                        <Avatar>
                          {participant?.username?.[0]?.toUpperCase() || 'U'}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography fontWeight={conv.unread_count > 0 ? 'bold' : 'normal'}>
                            {participant?.full_name || participant?.username || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {conv.last_message && formatTime(conv.last_message.created_at)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          fontWeight={conv.unread_count > 0 ? 'bold' : 'normal'}
                        >
                          {conv.last_message?.content || 'No messages yet'}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })
            )}
          </List>
        </Box>

        {/* Chat Window */}
        <Box
          sx={{
            flex: 1,
            display: { xs: selectedConversation ? 'flex' : 'none', md: 'flex' },
            flexDirection: 'column',
            height: '100%'
          }}
        >
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ mr: 2 }}>
                    {selectedConversation.participants[0]?.username?.[0]?.toUpperCase() || 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {selectedConversation.participants[0]?.full_name ||
                       selectedConversation.participants[0]?.username || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Active
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Messages */}
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                  backgroundColor: '#fafafa',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {messages.map((msg, index) => {
                  const isOwnMessage = msg.sender_id !== selectedConversation.participants[0]?.user_id;
                  const showDate = index === 0 ||
                    new Date(messages[index - 1].created_at).toDateString() !== new Date(msg.created_at).toDateString();

                  return (
                    <React.Fragment key={msg.id}>
                      {showDate && (
                        <Box sx={{ textAlign: 'center', my: 2 }}>
                          <Typography variant="caption" sx={{ backgroundColor: '#e0e0e0', px: 2, py: 0.5, borderRadius: 2 }}>
                            {new Date(msg.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                          mb: 1
                        }}
                      >
                        <Paper
                          elevation={1}
                          sx={{
                            p: 1.5,
                            maxWidth: '70%',
                            backgroundColor: isOwnMessage ? '#1976d2' : '#fff',
                            color: isOwnMessage ? '#fff' : '#000',
                            borderRadius: 2,
                            wordBreak: 'break-word'
                          }}
                        >
                          <Typography variant="body2">{msg.content}</Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              textAlign: 'right',
                              mt: 0.5,
                              opacity: 0.7
                            }}
                          >
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Paper>
                      </Box>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>

              {/* Message Input */}
              <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Type a message..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={sending}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() || sending}
                    sx={{
                      backgroundColor: '#1976d2',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#1565c0'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                  >
                    {sending ? <CircularProgress size={24} /> : <SendIcon />}
                  </IconButton>
                </Box>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 4
              }}
            >
              <MessageIcon sx={{ fontSize: 100, color: 'text.secondary', mb: 3 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                Select a conversation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a conversation from the list to start messaging
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* New Chat FAB */}
      <Fab
        color="primary"
        aria-label="new chat"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setNewChatDialog(true)}
      >
        <AddIcon />
      </Fab>

      {/* New Chat Dialog */}
      <Dialog open={newChatDialog} onClose={() => setNewChatDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Conversation</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={users}
            getOptionLabel={(option) => option.username || ''}
            value={selectedUser}
            onChange={(e, newValue) => setSelectedUser(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Select user" margin="normal" fullWidth />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Avatar sx={{ mr: 2 }}>{option.username?.[0]?.toUpperCase()}</Avatar>
                <Box>
                  <Typography>{option.username}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewChatDialog(false)}>Cancel</Button>
          <Button onClick={handleNewConversation} variant="contained" disabled={!selectedUser}>
            Start Chat
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Messages;
